import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, CARD_SECTION_TITLE_CLASS, Modal, type BadgeTone } from '@safetyhub/ui';
import { companiesApi, type Company } from '../../lib/companies';
import {
  companyMergeApi,
  type CompanyMergeRequest,
  type CompanyMergeRequestStatus,
} from '../../lib/company-merge';
import { extractApiError } from '../../lib/api';
import { useToastStore } from '../../stores/toast';
import { MODAL_LAYER, type ModalLayer } from '../modalLayers';

interface TenantMergeSectionProps {
  /** 자기 회사 id (= 조직 id). */
  companyId: string;
  /** 자기 회사 사업자등록번호 — 같은 번호의 다른 조직을 찾는 축이다. */
  registrationNumber?: string;
  /** 클레임 확정 시각. null이면 정본이 아니라 통합을 *요청할* 자격이 없다(받는 건 별개). */
  claimedAt?: string | null;
  /** 이 섹션이 여는 모달의 층. 페이지에서 직접 열므로 기본은 root. */
  layer?: ModalLayer;
}

const STATUS_LABEL: Record<CompanyMergeRequestStatus, string> = {
  PENDING: '응답 대기',
  ACCEPTED: '수락됨 · 통합 처리 중',
  EXECUTED: '통합 완료',
  REJECTED: '거절됨',
  CANCELLED: '취소됨',
  EXPIRED: '기간 만료',
};

const STATUS_TONE: Record<CompanyMergeRequestStatus, BadgeTone> = {
  PENDING: 'amber',
  ACCEPTED: 'blue',
  EXECUTED: 'emerald',
  REJECTED: 'rose',
  CANCELLED: 'slate',
  EXPIRED: 'slate',
};

/** 아직 결론이 나지 않은 요청 — 같은 대상에 새 요청을 낼 수 없는 상태(서버 중복 금지 축과 동일). */
const isInFlight = (status: CompanyMergeRequestStatus) =>
  status === 'PENDING' || status === 'ACCEPTED';

/**
 * 조직 통합(테넌트 흡수) 요청 — 클레임 정본 조직이 같은 사업자번호로 따로 운영 중인 조직에
 * 통합을 요청하는 자리.
 *
 * 클레임(P4-A)은 **빈 껍데기 파편만** 흡수하고 사람·업무 데이터가 매달린 조직은 건너뛴다.
 * 그렇게 남은 조직은 "합쳐도 되는지"를 그쪽이 답해야 옮길 수 있다 — 그래서 이 흐름은
 * 자동이 아니라 상대 조직 OWNER의 명시적 수락을 받는다(무응답은 7일 뒤 만료).
 *
 * 후보 조회 전용 API는 서버에 없다. 같은 사업자번호 조회(GET /companies)로 후보를 뽑고,
 * "운영 실체를 가진 테넌트인가"의 최종 판정은 서버 요청 시점에 이뤄진다
 * (파편이면 COMPANY_MERGE_TARGET_NOT_TENANT로 거절된다).
 *
 * **양방향이다.** 보내는 쪽(정본)뿐 아니라 **받는 쪽**도 여기서 본다 — 받는 조직은 정본이
 * 아니라서 요청 자격이 없지만, 자신에게 온 요청은 확인할 수 있어야 한다. 그 길이 알림뿐이면
 * 알림을 놓치거나 다른 조직 컨텍스트에 있을 때(알림은 조직 스코프) 수락 화면에 영영 닿지
 * 못한다. 목록 API가 이미 양방향(canonical/target)을 주므로 방향은 응답에서 갈라 쓴다.
 */
export default function TenantMergeSection({
  companyId,
  registrationNumber,
  claimedAt,
  layer = MODAL_LAYER.root,
}: TenantMergeSectionProps) {
  const { showToast } = useToastStore();
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState<Company[]>([]);
  const [requests, setRequests] = useState<CompanyMergeRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [target, setTarget] = useState<Company | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cancelingId, setCancelingId] = useState<string | null>(null);

  const isClaimed = Boolean(claimedAt);
  /** 통합을 *보낼* 자격 — 정본(클레임 완료) + 사업자번호가 있어야 후보를 찾을 수 있다. */
  const canRequest = isClaimed && Boolean(registrationNumber);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // 목록은 자격과 무관하게 읽는다 — 받은 요청은 정본이 아닌 조직에도 온다.
      const [search, list] = await Promise.all([
        canRequest
          ? companiesApi.search({ registrationNumber, limit: 20 })
          : Promise.resolve({ items: [] as Company[], nextCursor: null }),
        companyMergeApi.list(companyId),
      ]);
      // 흡수가 끝난 조직은 soft delete라 검색에서 이미 빠진다. 여기서 빼는 것은 자기 자신과
      // 아직 결론이 안 난 요청의 대상 — 후자는 아래 진행 목록이 이미 말하고 있다.
      const inFlightTargetIds = new Set(
        list.filter((r) => isInFlight(r.status)).map((r) => r.targetCompanyId),
      );
      setCandidates(
        search.items.filter((c) => c.id !== companyId && !inFlightTargetIds.has(c.id)),
      );
      setRequests(list);
    } catch (err) {
      // 조회 실패를 조용히 빈 상태로 두면 "통합할 게 없다"로 읽힌다 — 말로 구분한다.
      showToast(extractApiError(err).message, 'error');
      setCandidates([]);
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [canRequest, companyId, registrationNumber, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRequest = async () => {
    if (!target) return;
    setIsSubmitting(true);
    try {
      await companyMergeApi.create(companyId, target.id);
      showToast('통합 요청을 보냈습니다. 상대 조직이 수락하면 통합됩니다.', 'success');
      setTarget(null);
      await load();
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (requestId: string) => {
    setCancelingId(requestId);
    try {
      await companyMergeApi.cancel(requestId);
      showToast('통합 요청을 취소했습니다.', 'success');
      await load();
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setCancelingId(null);
    }
  };

  // 받은 요청 중 아직 답하지 않은 것 — 이 자리의 존재 이유(수락 화면 진입점)다.
  const incomingPending = requests.filter(
    (r) => r.targetCompanyId === companyId && r.status === 'PENDING',
  );
  const incomingPendingIds = new Set(incomingPending.map((r) => r.id));
  const history = requests.filter((r) => !incomingPendingIds.has(r.id));

  // 후보도 이력도 없으면 자리를 차지하지 않는다 — 대부분의 조직에서 평생 빈 섹션이다.
  if (!isLoading && candidates.length === 0 && requests.length === 0) return null;

  return (
    <Card padding="xl" radius="lg" elevation="md">
      <h3 className={`${CARD_SECTION_TITLE_CLASS} mb-1`}>조직 통합</h3>
      <p className="mb-4 text-sm text-ink-500">
        {canRequest
          ? '같은 사업자번호로 따로 운영 중인 조직이 있으면 하나로 합칠 수 있습니다. 상대 조직이 수락해야 진행되며, 수락 시 그 조직의 그룹·현장·구성원·문서가 모두 이 조직으로 옮겨지고 상대 조직은 사라집니다.'
          : '같은 사업자번호로 인증된 조직이 이 조직을 흡수하겠다고 요청하면 여기에 표시됩니다. 수락 여부는 이 조직의 소유자가 직접 결정합니다.'}
      </p>

      {isLoading ? (
        <p className="text-sm text-ink-400">불러오는 중...</p>
      ) : (
        <div className="space-y-5">
          {incomingPending.length > 0 && (
            <div className="rounded-card border border-amber-200 bg-amber-50 p-3">
              <p className="mb-2 text-sm font-medium text-amber-900">
                받은 통합 요청 ({incomingPending.length})
              </p>
              <ul className="space-y-2">
                {incomingPending.map((request) => (
                  <li
                    key={request.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-amber-200 bg-white px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink-900">
                        {request.canonicalCompany?.name ?? '(이름 없음)'}
                      </p>
                      <p className="text-xs text-ink-500">
                        {new Date(request.expiresAt).toLocaleDateString('ko-KR')}까지 응답하지
                        않으면 만료됩니다
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/company-merge/${request.token}`)}
                    >
                      요청 확인
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {candidates.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-ink-700">통합할 수 있는 조직</p>
              <ul className="space-y-2">
                {candidates.map((company) => (
                  <li
                    key={company.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink-900">{company.name}</p>
                      <p className="text-xs text-ink-500">
                        {company.registrationNumber ?? '사업자번호 없음'}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setTarget(company)}
                    >
                      통합 요청
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 답을 기다리는 수신 건은 위 블록이 이미 말했다 — 이력에 또 싣지 않는다. */}
          {history.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-ink-700">통합 요청 이력</p>
              <ul className="space-y-2">
                {history.map((request) => {
                  const isOutgoing = request.canonicalCompanyId === companyId;
                  const counterpart = isOutgoing
                    ? request.targetCompany?.name
                    : request.canonicalCompany?.name;
                  return (
                    <li
                      key={request.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm text-ink-900">
                          <span className="text-ink-500">{isOutgoing ? '보냄 · ' : '받음 · '}</span>
                          {counterpart ?? '(이름 없음)'}
                        </p>
                        <p className="text-xs text-ink-500">
                          {new Date(request.createdAt).toLocaleDateString('ko-KR')} 요청
                          {request.status === 'PENDING' &&
                            ` · ${new Date(request.expiresAt).toLocaleDateString('ko-KR')} 만료`}
                          {request.rejectReason ? ` · 사유: ${request.rejectReason}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge tone={STATUS_TONE[request.status]}>
                          {STATUS_LABEL[request.status]}
                        </Badge>
                        {isOutgoing && request.status === 'PENDING' && (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => handleCancel(request.id)}
                            disabled={cancelingId === request.id}
                          >
                            {cancelingId === request.id ? '취소 중...' : '취소'}
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      <Modal
        open={target !== null}
        onClose={() => {
          if (!isSubmitting) setTarget(null);
        }}
        title="조직 통합 요청"
        size="md"
        overlayZClassName={layer}
        closeOnOverlay={!isSubmitting}
        closeOnEsc={!isSubmitting}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setTarget(null)}
              disabled={isSubmitting}
            >
              취소
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleRequest}
              disabled={isSubmitting}
            >
              {isSubmitting ? '보내는 중...' : '통합 요청 보내기'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="rounded-card bg-ink-50 px-3 py-2">
            <p className="text-xs text-ink-500">통합할 조직</p>
            <p className="text-sm font-semibold text-ink-900">{target?.name}</p>
            <p className="text-xs text-ink-500">{target?.registrationNumber ?? ''}</p>
          </div>
          <div className="space-y-2 text-sm text-ink-600">
            <p>요청을 보내면 상대 조직의 소유자에게 알림이 갑니다. 진행되려면:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>상대 조직 소유자가 직접 수락해야 합니다. 자동으로 진행되지 않습니다.</li>
              <li>7일 안에 응답이 없으면 요청은 만료됩니다.</li>
              <li>
                수락하면 그 조직의 그룹·현장·구성원·점검·보고서가 모두 이 조직으로 옮겨지고,
                <strong className="text-ink-800"> 그 조직은 사라집니다.</strong> 되돌릴 수 없습니다.
              </li>
              <li>상대 조직의 구독은 통합과 함께 해지됩니다.</li>
            </ul>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
